import myw from 'myWorld/base/core';
import L from 'leaflet';

const trace = myw.trace.bind(myw.trace, 'layer');

myw.MywVectorLayerQuery = myw.MywClass.extend('MywVectorLayerQuery', {
    /*
     * @class  Class to perform queries to fetch data for layer refresh. This implementation invokes the
     * render request for each feature in the layer.
     * @param  {MywVectorLayer} owner    [description]
     * @param  {MyWorldDatasource} datasource [description]
     * @constructs
     */
    initialize(owner, datasource) {
        this.owner = owner;
        this.datasource = datasource;

        /* holds ID of currently active update chain. Any update chain with ID of less
         * than this should be terminated.
         * @type {integer} */
        this.currentUpdateId = 0;

        /*
         * Time it took last query to run in milliseconds
         * @type {integer}
         */
        this.lastQueryTime = 0;
        this.startTime = 0;
    },

    /*
     * Initiate the queries to update the layer. Sends a request to the server
     * that returns the feature in the layer within the bounds of the map. Subsequent requests
     * with different offsets might be sent if the number returned is greater than the fetch limit.
     * For dynamic layers, we query the whole map view bounds. For static layers, we query only new areas
     * of the map that come into view (and so send a list of bounds). If the user zooms in then no request is
     * sent as all the required features are on the map.
     * @param  {boolean} redraw Whether the existing feature representations should be refreshed and redrawn as well.
     *                          This will take into account style or position changes for a feature.
     */
    async update(redraw) {
        const tableNames = this.owner.getFeatureTypes().slice(0),
            mapBounds = this.owner.map.getBounds(),
            params = {
                bounds: mapBounds,
                offset: 0,
                limit: this.owner.options.updateFetchLimit,
                layerName: this.owner.options.name,
                zoom: this.owner.map.getZoom(),
                schema: this.owner.options.schema
            };

        let queryBounds = [mapBounds];
        if (!redraw && this.lastBounds) {
            queryBounds = this.subtractBounds(mapBounds, this.lastBounds);
        }
        if (myw.isTracing('layer', 10)) this._debugDrawOnMap(queryBounds, 'red');

        if (queryBounds.length === 0) {
            if (myw.isTracing('layer', 10)) this._debugDrawOnMap([this.lastBounds]);
            return;
        }

        this.owner.fire('rendering-started');

        if (this.owner.options.worldName) params.world_name = this.owner.options.worldName;

        trace(3, `Layer update start '${this.owner.options.name}' (updateId ${this.currentUpdateId+1})`);
        this.startTime = new Date();

        try {
            // We need to do this here as timing issues may mean that DD information is not available when we need it
            // to get styles.
            await this.datasource.getDDInfoFor(tableNames);
            this.currentUpdateId += 1;
            const updateId = this.currentUpdateId;
            params.bounds = queryBounds;
            params.mapBounds = mapBounds;
            // Determine the features that will be displayed given the current map bounds.
            const features = await this.datasource.getLayerFeatures(params);
            const responseId = await this.handleLayerFeaturesResultsChain(
                redraw,
                params,
                updateId,
                features
            );

            //for performance, remove from the map representations that are outside the 'pannable'
            //area around the map
            if (this.currentUpdateId == responseId) this.owner.gcFeatureRep(mapBounds, responseId);
            this.lastQueryTime = new Date() - this.startTime;
            trace(3, `Layer update end '${this.owner.options.name}'`);
            this.owner.fire('rendering-ended');
        } catch (e) {
            console.log(
                `Error rendering layer ${this.owner.options.name}: ${e.name} ${e.message} ${e.stack}`
            );
            this.owner.fire('rendering-ended');
        }
    },

    getFeaturesFromLastBounds(featureTypes) {
        const params = {
            bounds: this.lastBounds,
            offset: 0,
            limit: this.owner.options.updateFetchLimit,
            layerName: this.owner.options.name,
            zoom: this.owner.map.getZoom(),
            featureTypes: featureTypes || []
        };

        if (this.owner.options.worldName) params.world_name = this.owner.options.worldName;

        this.datasource.getLayerFeatures(params).then(data => {
            this.owner.handleLayerFeaturesResults(true, data.features, this.currentUpdateId);
        });
    },

    _debugDrawOnMap(queryBounds, color) {
        // For debugging - draw the damage areas
        (this.boundsOnMap || []).forEach(b => {
            this.owner.map.removeLayer(b);
        });

        this.boundsOnMap = [];
        queryBounds.forEach(bound => {
            const pts = [
                bound.getSouthWest(),
                bound.getNorthWest(),
                bound.getNorthEast(),
                bound.getSouthEast(),
                bound.getSouthWest()
            ];

            const b = L.polygon(pts, {
                fill: true,
                fillColor: color || 'green',
                fillOpactity: 0.5
            });
            this.boundsOnMap.push(b);
            this.owner.map.addLayer(b);
        });
    },

    /*
     * Subtracts a bounding box from another one
     * @param  {L.LatLngBounds} bounds1     Bounding box being subtracted from
     * @param  {L.LatLngBounds} bounds2     Bounding box to subtract/exclude from the first one
     * @return {Array<L.LatLngBounds>} List of bounding boxes that make up the result of the subtraction
     */
    subtractBounds(bounds1, bounds2) {
        let left, right, top, bottom;
        const bounds = [];

        if (!bounds1.intersects(bounds2)) return [bounds1];

        // Determine where edges of bounds2 cross bounds1
        if (bounds2.getWest() >= bounds1.getWest() && bounds2.getWest() <= bounds1.getEast())
            left = bounds2.getWest();

        if (bounds2.getEast() >= bounds1.getWest() && bounds2.getEast() <= bounds1.getEast())
            right = bounds2.getEast();

        if (bounds2.getSouth() >= bounds1.getSouth() && bounds2.getSouth() <= bounds1.getNorth())
            bottom = bounds2.getSouth();

        if (bounds2.getNorth() >= bounds1.getSouth() && bounds2.getNorth() <= bounds1.getNorth())
            top = bounds2.getNorth();

        // Calculate left and right strips
        if (left) {
            bounds.push(L.latLngBounds(bounds1.getSouthWest(), L.latLng(bounds1.getNorth(), left)));
        }
        if (right) {
            bounds.push(
                L.latLngBounds(L.latLng(bounds1.getSouth(), right), bounds1.getNorthEast())
            );
        }

        // Calculate top and bottom strips and truncate if there are left and right strips
        let west, east;
        if (top) {
            if (left) west = left;
            else west = bounds1.getWest();
            if (right) east = right;
            else east = bounds1.getEast();
            bounds.push(L.latLngBounds(L.latLng(top, west), L.latLng(bounds1.getNorth(), east)));
        }
        if (bottom) {
            if (left) west = left;
            else west = bounds1.getWest();
            if (right) east = right;
            else east = bounds1.getEast();
            bounds.push(L.latLngBounds(L.latLng(bounds1.getSouth(), west), L.latLng(bottom, east)));
        }

        return bounds;
    },

    /*
     * Processes a list of features obtained from the database. Initiate
     * another query if there are further results to fetch.
     * @param  {boolean}    redraw          Whether the existing feature representations should be refreshed as well
     * @param  {string[]}   tableNames      List of remaining features types to fetch
     * @param  {object}     params          Parameters of the update
     * @param  {integer}    updateId        ID of this update chain
     * @param  {Array<DDFeature>} features   Features obtained from the database
     */
    handleLayerFeaturesResultsChain(redraw, params, updateId, data) {
        // If no other update chain has been started, process the results and do another query
        if (this.currentUpdateId == updateId) {
            if (data.offset) {
                const newParams = { ...params, offset: data.offset };
                //initiate next request before passing results to layer (for feature rep updates)
                const nextRequestPromise = this.datasource
                    .getLayerFeatures(newParams)
                    .then(
                        this.handleLayerFeaturesResultsChain.bind(this, redraw, newParams, updateId)
                    );
                //pass features to layer so it can update the feature representations
                this.owner.handleLayerFeaturesResults(redraw, data.features, updateId);
                return nextRequestPromise;
            } else {
                // We have completed the chain

                this.owner.handleLayerFeaturesResults(redraw, data.features, updateId);

                this.lastBounds = params.mapBounds;

                if (redraw) {
                    // we do know which items have been deleted.
                    // As this is a redraw there will have been only one bounds
                    this.owner.removeDeletedFeatures(params.bounds[0], updateId);
                }
                trace(
                    3,
                    `Layer update end cleanly '${this.owner.options.name}' updateId=${updateId}`
                );
            }
        } else {
            trace(
                3,
                `Layer update end aborted '${this.owner.options.name}' updateId=${updateId} currentUpdateId=${this.currentUpdateId}`
            );
        }

        return Promise.resolve(updateId);
    }
});

export default myw.MywVectorLayerQuery;
